import React from "react";
import { useSelector } from "react-redux";

import { selectProfitability } from "../../../../slices/report/selectors";
import useReportFormat from "../useReportFormat";

const cardBase = {
  background: "white",
  borderRadius: 12,
  padding: 24,
  boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
  border: "1px solid #f0f4ff",
};
const labelStyle = {
  fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.1em", color: "#64748b", marginBottom: 0,
};
const valueStyle = {
  fontSize: "1.75rem", fontWeight: 800, color: "#002045",
  letterSpacing: "-0.03em", margin: 0,
};
const sectionLabel = {
  fontSize: "0.6rem", fontWeight: 800, textTransform: "uppercase",
  letterSpacing: "0.12em", color: "#94a3b8", marginBottom: 12, marginTop: 0,
  paddingBottom: 8, borderBottom: "1px solid #f1f5f9",
};

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v.toFixed(1)}%`);
const clampPct = (v) => Math.max(0, Math.min(100, v || 0));

const IconChip = ({ icon, bg, color }) => (
  <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: 10, background: bg, color }}>
    <i className={icon} style={{ fontSize: "1.15rem" }} />
  </div>
);

// Light metric card (ROI / ROE / Capital)
const MetricCard = ({ icon, iconBg, iconColor, label, value, sub, subValue, barPct, barColor }) => (
  <div style={cardBase}>
    <div className="d-flex justify-content-between align-items-start" style={{ marginBottom: 16 }}>
      <IconChip icon={icon} bg={iconBg} color={iconColor} />
      {sub && <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8" }}>{sub}</span>}
    </div>
    <p style={labelStyle}>{label}</p>
    <div className="d-flex align-items-baseline gap-2" style={{ marginTop: 8 }}>
      <span style={valueStyle}>{value}</span>
      {subValue && <span style={{ fontSize: "0.8rem", color: "#94a3b8", fontWeight: 600 }}>{subValue}</span>}
    </div>
    {barPct !== undefined && (
      <div style={{ height: 4, background: "#e2e8f0", borderRadius: 99, overflow: "hidden", marginTop: 16 }}>
        <div style={{ height: "100%", width: `${clampPct(barPct)}%`, background: barColor, borderRadius: 99, transition: "width 0.4s ease" }} />
      </div>
    )}
  </div>
);

// Highlighted (dark) Net Profit card
const ProfitCard = ({ value, sub }) => (
  <div style={{ ...cardBase, background: "linear-gradient(135deg,#002045 0%,#0a3a6b 100%)", border: "none", color: "#fff", boxShadow: "0 10px 30px -8px rgba(0,32,69,0.45)" }}>
    <div className="d-flex justify-content-between align-items-start" style={{ marginBottom: 16 }}>
      <IconChip icon="ri-funds-line" bg="rgba(255,255,255,0.12)" color="#fff" />
    </div>
    <p style={{ ...labelStyle, color: "rgba(255,255,255,0.75)" }}>Net Profit (projected)</p>
    <div style={{ ...valueStyle, color: "#fff", marginTop: 8 }}>{value}</div>
    {sub && (
      <p style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", margin: "16px 0 0" }} className="d-flex align-items-center gap-1">
        <i className="ri-bar-chart-2-line" />{sub}
      </p>
    )}
  </div>
);

const OverviewView = ({ phaseId }) => {
  const formatCurrency = useReportFormat();
  const p = useSelector(selectProfitability);

  if (!p) {
    return <div className="text-muted">No profitability data available for this project.</div>;
  }

  const profitPositive = (p.profit ?? 0) >= 0;
  const margin = p.revenue ? (p.profit / p.revenue) * 100 : null;

  return (
    <div>
      <p style={sectionLabel}>Profitability</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 24, marginBottom: 32 }}>
        <MetricCard
          icon="ri-line-chart-line" iconBg="#e6edf7" iconColor="#002045"
          label="ROI — Return on Investment"
          value={fmtPct(p.roi)}
          subValue={p.profit == null ? null : formatCurrency(p.profit)}
          barPct={p.roi} barColor="#002045"
        />
        <MetricCard
          icon="ri-bank-line" iconBg="#efe9fb" iconColor="#7c3aed"
          label="ROE — Return on Equity"
          value={fmtPct(p.roe)}
          barPct={p.roe} barColor="#7c3aed"
        />
        <MetricCard
          icon="ri-money-euro-circle-line" iconBg="#f1f5f9" iconColor="#334155"
          label={phaseId ? "Phase Equity" : "Total Capital (all phases)"}
          value={p.equity == null ? "—" : formatCurrency(p.equity)}
        />
        <ProfitCard
          value={p.profit == null ? "—" : formatCurrency(p.profit)}
          sub={margin == null ? null : `${margin.toFixed(1)}% margin on listing`}
        />
      </div>

      <p style={sectionLabel}>Basis (projected)</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 24 }}>
        <MetricCard
          icon="ri-price-tag-3-line" iconBg="#ecfdf5" iconColor="#059669"
          label="Projected Revenue (listing)"
          value={p.revenue == null ? "—" : formatCurrency(p.revenue)}
        />
        <MetricCard
          icon="ri-wallet-3-line" iconBg="#fef2f2" iconColor="#ef4444"
          label="Total Cost (budget)"
          value={p.cost == null ? "—" : formatCurrency(p.cost)}
        />
        {/* <MetricCard
          icon="ri-coins-line" iconBg={profitPositive ? "#ecfdf5" : "#fef2f2"} iconColor={profitPositive ? "#059669" : "#ef4444"}
          label="Projected Profit"
          value={p.profit == null ? "—" : formatCurrency(p.profit)}
        /> */}
      </div>
    </div>
  );
};

export default OverviewView;
